import { useState, useRef, useEffect } from "react";
import { ChevronDown, Search, Check, Columns } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Input } from "./input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";

export interface GroupByField {
  key: string;
  label: string;
  values: string[];
}

interface GroupBySelectorProps {
  fields: GroupByField[];
  selectedField: string;
  onFieldChange: (fieldKey: string) => void;
}

export const GroupBySelector = ({
  fields,
  selectedField,
  onFieldChange,
}: GroupBySelectorProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = fields.filter((f) =>
    f.label.toLowerCase().includes(query.toLowerCase())
  );

  const displayed = showAll ? filtered : filtered.slice(0, 5);
  const hasMore = filtered.length > 5 && !showAll;

  const selectedLabel =
    fields.find((f) => f.key === selectedField)?.label || selectedField;

  useEffect(() => {
    if (open) {
      setQuery("");
      setShowAll(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 text-sm">
          <Columns className="h-3.5 w-3.5" />
          Group: {selectedLabel}
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-56 p-0 bg-popover border border-border shadow-popover"
      >
        {/* Search */}
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              ref={inputRef}
              placeholder="Search fields..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowAll(false);
              }}
              className="pl-8 h-7 text-sm"
            />
          </div>
        </div>

        {/* Fields List */}
        <div className="py-1 max-h-52 overflow-y-auto">
          {displayed.map((field) => (
            <button
              key={field.key}
              onClick={() => {
                onFieldChange(field.key);
                setOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-left hover:bg-muted transition-colors",
                selectedField === field.key &&
                  "bg-accent-tint text-primary"
              )}
            >
              <span className="flex-1 truncate">{field.label}</span>
              {selectedField === field.key && (
                <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
              )}
            </button>
          ))}
          {displayed.length === 0 && (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">
              No fields found
            </div>
          )}
        </div>

        {/* View more */}
        {hasMore && (
          <div className="border-t border-border p-1">
            <button
              onClick={() => setShowAll(true)}
              className="w-full text-xs text-primary hover:underline py-1.5 text-center"
            >
              View {filtered.length - 5} more fields
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
